import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Send,
  Search,
  Moon,
  Sun,
  Droplets,
  FileText,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { XiaBar } from '../components/ui/XiaBar';
import { useAppStore } from '../store/appStore';

/* --------------------------------------------------------------------------
   GUÍA DE ESTILO VIVA de ÁGORA.
   Regla dura: CERO hex literal en este archivo. Las muestras de color leen su
   propio valor resuelto en runtime (getComputedStyle) desde los tokens CSS.
   -------------------------------------------------------------------------- */

function rgbToHex(rgb: string): string {
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return rgb;
  const [r, g, b] = match.map((n) => Number(n));
  const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

interface SwatchSpec {
  cssVar: string;
  nombre: string;
  rol: string;
  /** Si el color es oscuro, la etiqueta va en blanco. */
  dark?: boolean;
}

function ColorSwatch({ spec }: { spec: SwatchSpec }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hex, setHex] = useState('');

  useLayoutEffect(() => {
    if (ref.current) {
      const resolved = getComputedStyle(ref.current).backgroundColor;
      setHex(rgbToHex(resolved));
    }
  });

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-raised shadow-sm">
      <div
        ref={ref}
        className="h-20 w-full"
        style={{ backgroundColor: `var(${spec.cssVar})` }}
        aria-hidden="true"
      />
      <div className="px-3 py-2">
        <p className="font-display text-sm font-semibold text-content">{spec.nombre}</p>
        <p className="text-xs text-content-muted">{spec.rol}</p>
        <p className="mt-1 font-sans text-xs font-medium tabular-nums text-content-muted">
          {hex}
        </p>
      </div>
    </div>
  );
}

const swatches: SwatchSpec[] = [
  { cssVar: '--color-navy-900', nombre: 'Azul Ágora', rol: 'Tinta / base oscura', dark: true },
  { cssVar: '--color-primary-600', nombre: 'Azul Participación', rol: 'Acción primaria', dark: true },
  { cssVar: '--color-cyan-500', nombre: 'Cian Conexión', rol: 'Datos en vivo' },
  { cssVar: '--color-violet-500', nombre: 'Violeta Inteligencia', rol: 'Acento IA', dark: true },
  { cssVar: '--color-ice-100', nombre: 'Gris Evidencia', rol: 'Fondos / bordes' },
  { cssVar: '--color-recognition', nombre: 'Ámbar Reconocimiento', rol: 'Coautoría' },
];

const semanticos: SwatchSpec[] = [
  { cssVar: '--color-success', nombre: 'Éxito', rol: 'Aporte guardado' },
  { cssVar: '--color-warning', nombre: 'Atención', rol: 'Cierre próximo' },
  { cssVar: '--color-error', nombre: 'Error', rol: 'Sin conexión' },
  { cssVar: '--color-info', nombre: 'Info', rol: 'Nota de proceso' },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-8">
      <h2 className="mb-4 font-display text-2xl font-semibold text-content">{title}</h2>
      {children}
    </section>
  );
}

export function StyleguidePage() {
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const [nombre, setNombre] = useState('');

  // Aplica el tema al documento para que los tokens de superficie cambien.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <div className="min-h-screen bg-surface-base">
      {/* Encabezado con logo positivo (wordmark sobre claro) + toggle de tema */}
      <header className="border-b border-border bg-surface-raised">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <img
            src="/brand/logopositivo.png"
            alt="ÁGORA"
            className="h-8 w-auto dark:hidden"
          />
          <img
            src="/brand/agoranegativo.png"
            alt="ÁGORA"
            className="hidden h-8 w-auto dark:block"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={toggleTheme}
            leadingIcon={
              theme === 'light' ? (
                <Moon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Sun className="h-4 w-4" aria-hidden="true" />
              )
            }
            aria-label={theme === 'light' ? 'Activar tema oscuro' : 'Activar tema claro'}
          >
            {theme === 'light' ? 'Cabina' : 'Bolsillo'}
          </Button>
        </div>
      </header>

      {/* Hero de marca — uso PUNTUAL del gradiente (splash del sistema de diseño) */}
      <div className="bg-brand-gradient">
        <div className="mx-auto flex max-w-5xl flex-col items-start gap-3 px-4 py-12">
          <img
            src="/brand/logonegativo.png"
            alt=""
            aria-hidden="true"
            className="h-14 w-auto"
          />
          <h1 className="font-display text-3xl font-bold text-content-inverse">
            Sistema de diseño ÁGORA
          </h1>
          <p className="max-w-xl font-sans text-base text-content-inverse">
            La plaza digital de la institución: cada voz queda en el acta y la
            deliberación termina en una propuesta de proyecto trazable.
          </p>
        </div>
      </div>

      <Section title="Logo institucional">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-center rounded-lg border border-border bg-surface-raised p-8">
            <img src="/brand/logopositivo.png" alt="Wordmark ÁGORA sobre claro" className="h-12 w-auto" />
          </div>
          <div className="flex items-center justify-center rounded-lg border border-border bg-navy p-8">
            <img src="/brand/logonegativo.png" alt="Símbolo ÁGORA sobre navy" className="h-12 w-auto" />
          </div>
        </div>
        <p className="mt-3 text-sm text-content-muted">
          El símbolo institucional va sin contenedor; el cuadrado redondeado navy
          se reserva al ícono de app.
        </p>
      </Section>

      <Section title="Paleta de marca">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {swatches.map((s) => (
            <ColorSwatch key={s.cssVar} spec={s} />
          ))}
        </div>
      </Section>

      <Section title="Colores semánticos">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {semanticos.map((s) => (
            <ColorSwatch key={s.cssVar} spec={s} />
          ))}
        </div>
      </Section>

      <Section title="Gradiente de marca">
        <div className="flex h-32 items-end rounded-lg bg-brand-gradient p-4">
          <p className="font-sans text-sm font-medium text-content-inverse">
            Azul Participación → Cian Conexión → Violeta Inteligencia · recurso
            puntual (hero / splash / símbolo), nunca fondo genérico.
          </p>
        </div>
      </Section>

      <Section title="Tipografía">
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-surface-raised p-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
              Sora · Display
            </p>
            <p className="font-display text-4xl font-bold text-content">Proceso participativo</p>
            <p className="font-display text-2xl font-semibold text-content">Propuesta de proyecto trazable</p>
            <p className="font-display text-xl font-medium text-content">Sesión de la Cabina</p>
          </div>
          <div className="rounded-lg border border-border bg-surface-raised p-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
              Inter · Texto
            </p>
            <p className="font-sans text-base text-content">
              Cada afirmación de la propuesta cita el aporte que la respalda. Lo
              que la IA no puede respaldar se marca como no respaldado.
            </p>
            <p className="font-sans text-sm text-content-muted">
              14px — metadatos, ayudas y etiquetas de la Cabina densa.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Botones">
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" leadingIcon={<Send className="h-4 w-4" aria-hidden="true" />}>
            Enviar aporte
          </Button>
          <Button variant="secondary" leadingIcon={<Search className="h-4 w-4" aria-hidden="true" />}>
            Buscar expediente
          </Button>
          <Button variant="ghost">Cancelar</Button>
          <Button variant="danger">Retirar aporte</Button>
          <Button variant="primary" loading>
            Guardando
          </Button>
          <Button variant="primary" disabled>
            No disponible
          </Button>
        </div>
      </Section>

      <Section title="Campos de formulario">
        <div className="grid max-w-md gap-4">
          <Input
            label="Tu aporte a la lluvia de ideas"
            placeholder="Escribí una idea sobre el agua potable del municipio"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            hint="Contá una idea concreta; después el grupo la agrupa."
          />
          <Input
            label="Correo institucional"
            type="email"
            leadingIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            placeholder="nombre@muni.gob.gt"
            error="Ingresá un correo válido para recibir tu certificado de coautoría."
            required
          />
        </div>
      </Section>

      <Section title="Insignias">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral">Borrador</Badge>
          <Badge variant="primary">En sesión</Badge>
          <Badge variant="success">Aporte guardado</Badge>
          <Badge variant="warning">Cierra en 5 min</Badge>
          <Badge variant="error">Sin conexión</Badge>
          <Badge variant="info">Modo asíncrono</Badge>
          <Badge variant="reconocimiento" withIcon>
            Coautoría
          </Badge>
          <Badge variant="ia" withIcon>
            Síntesis IA
          </Badge>
        </div>
      </Section>

      <Section title="Tarjetas">
        <div className="grid gap-4 sm:grid-cols-2">
          <Card
            title="Agua potable · Zona 3"
            footer={
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" aria-hidden="true" /> 24 participantes
              </span>
            }
          >
            <p className="flex items-start gap-2 text-sm text-content">
              <Droplets className="mt-0.5 h-4 w-4 shrink-0 text-cyan" aria-hidden="true" />
              Proceso participativo para priorizar la red de agua del casco urbano.
              12 aportes ciudadanos vinculados a una iniciativa municipal.
            </p>
          </Card>
          <Card
            title="Expediente municipal digital"
            footer={<Badge variant="ia" withIcon>Hallazgo respaldado por 8 aportes</Badge>}
          >
            <p className="flex items-start gap-2 text-sm text-content">
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              Borrador de propuesta con formato tipo SEGEPLAN. Cada párrafo enlaza a
              su evidencia; lo no respaldado queda visible para el facilitador.
            </p>
          </Card>
        </div>
      </Section>

      <Section title="Barra XIA">
        <XiaBar
          que="Lluvia de ideas sobre agua potable"
          porQue="Reunir problemas reales antes de priorizar"
          hacia="Alimenta la propuesta de proyecto del municipio"
          tiempoRestante="08:30"
        />
      </Section>

      <footer className="border-t border-border px-4 py-8 text-center text-sm text-content-muted">
        ÁGORA · Grupo InteligencIA — sistema de diseño F0. Tokens en{' '}
        <code className="font-sans">src/styles/tokens.css</code>.
      </footer>
    </div>
  );
}
